import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Footer from 'views/molecules/footer';
import ebisLogo from 'assets/images/logo-2.svg';
import 'views/pages/acm-issue/acm-issue.scss';
import acmLogo from 'assets/images/acm-issue/acm_logo.svg';
import AcmActionBlurText from 'assets/images/acm-issue/acm_action_blur_text.png';
import AcmFactorBlurText from 'assets/images/acm-issue/acm_factor_blur_text.png';
import AcmFactorBlurTable from 'assets/images/acm-issue/acm_factor_blur_table.png';
import { Button } from 'react-bootstrap';
import pages from 'services/routes/pages';
import { DASHBOARD } from 'services/routes/constants';
import { getAccountId, getAgentFlag, getAiOptout } from 'store/auth/selectors';
import loggerActions from 'store/logger/actions';
import Spinner from 'views/atoms/loader/Spinner';
import { dashboardSelectors } from 'store/dashboard';
import { STATUS_IDLE, STATUS_LOADING } from 'store/helper';
import Markdown from 'react-markdown';
import { isEmpty } from 'lodash';
import dashboardActions from 'store/dashboard/actions';
import DashboardApi from 'services/api/DashboardApi';
import logging from 'domain/logging';
import gfm from 'remark-gfm';
import { fixCJKBold } from 'services/utils';
import AcmCtaIcon from 'assets/images/icon-caution-yellow.svg';
import AcmBannerImage from 'assets/images/acm-issue/acm_cta_register_banner.png';
import CloseIcon from 'assets/images/acm-issue/acm_close_white_icon.svg';
import AcmPRModal from './components/AcmPRModal';

function AcmIssueContainer() {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showPRModal, setShowPRModal] = useState(false);
  const isUserAgent = useSelector(getAgentFlag, shallowEqual);
  const aiOptout = useSelector(getAiOptout, shallowEqual);
  const accountId = useSelector(getAccountId, shallowEqual);
  const isLoading = useSelector(
    dashboardSelectors.getStatusAcmIssues,
    shallowEqual
  );
  const acmIssues = useSelector(dashboardSelectors.getAcmIssues, shallowEqual);

  const [acmIssueDetail, setAcmIssueDetail] = useState(null);
  const [isBannerShown, setIsBannerShown] = useState(true);

  const path = window.location.pathname;
  const id = path.split('/')[2];

  const fetchAcmIssues = useCallback(async () => {
    try {
      dispatch(dashboardActions.setAcmIssuesLoading(STATUS_LOADING));
      const response = await DashboardApi.fetchKpiBorder();
      const { data } = response.data;
      if (!isEmpty(data?.issues_card)) {
        dispatch(dashboardActions.fetchAcmIssuesSuccess(data.issues_card));
        dispatch(dashboardActions.setAcmIssuesLoading(STATUS_IDLE));
      }
    } catch (error) {
      logging.error('Error fetching ACM issues', error);
      dispatch(dashboardActions.setAcmIssuesLoading(STATUS_IDLE));
    }
  }, []);
  // Show permission denied modal if user has agent role or aiOptout
  useEffect(() => {
    if (isUserAgent || aiOptout) {
      dispatch(
        loggerActions.logConfirmModal({
          title: '本サービスに対する権限がありません',
          content: `
            <div>
              お使いのユーザーIDでは、この画面にアクセスする権限がありません。
              <br />
              ご契約元の管理者ユーザー様にご確認ください。
            </div>`,
          callback: () => {
            history.push(pages[DASHBOARD].path);
          },
        })
      );
    }
  }, [dispatch, history, isUserAgent, aiOptout]);

  useEffect(() => {
    fetchAcmIssues();
  }, []);

  useEffect(() => {
    if (!isEmpty(acmIssues)) {
      const acmIssueDetail = acmIssues.issues[id - 1];
      setAcmIssueDetail(acmIssueDetail);
    }
  }, [acmIssues]);

  // Show permission denied modal immediately if user doesn't have permission
  // Don't render content if no issue data or user doesn't have permission
  if (isUserAgent || aiOptout || !acmIssueDetail) {
    return null;
  }

  return (
    <>
      {isLoading === STATUS_LOADING ? (
        <div className="acm-issue-loading">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="acm-issue-container">
            {isBannerShown && (
              <div className="acm-banner-wrapper">
                <a
                  href={`https://docs.google.com/forms/d/e/1FAIpQLSeCv0nk9TNeV6556gOK813z3mDpttrh_VuVq_AHwVN7hy0DvA/viewform?entry.1032917813=1&entry.560226316=${accountId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  id="acm-issue-detail-cv"
                  className="acm-banner-link"
                >
                  <img src={AcmBannerImage} alt="acm banner" />
                </a>
                <Button onClick={() => setIsBannerShown(false)} className="acm-banner-close-button"><img src={CloseIcon} alt="close icon" /></Button>
              </div>
            )}
            <img src={ebisLogo} alt="AD Ebis Logo" className="acm-ebis-logo" />
            <div className="acm-title-wrapper">
              <div className="acm-title">
                <img src={acmLogo} alt="acm logo" />
                <p className="logo-title">課題発見エージェント（簡易版）</p>
              </div>
              <p className="acm-sub-title">
                貴社のアドエビスデータを元に課題をピックアップ。目標値は「直近3ヶ月平均」から自動算出されています。
              </p>
            </div>

            <div className="acm-issue-content">
              <div className="acm-cta-wrapper">
                <img
                  src={AcmCtaIcon}
                  alt="acm cta icon"
                  className="acm-cta-icon"
                />
                <div className="acm-cta-content">
                  <p className="cta-text">
                    この分析結果は、仮の目標値で生成した簡易版です。
                    <br />
                    完全版では、KPIや商材特性などをAIに学習させ、貴社の実情に即したレポートを提示します。
                  </p>
                  <a
                    href="https://cm.ebis.ne.jp/case/belong/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-link"
                    id="acm-issue-detail-voice"
                  >
                    生産性向上を体感されているアドエビスユーザー様のお声
                    <i className="far fa-external-link-alt" />
                  </a>
                </div>
              </div>
              <div className="issue-overview-wrapper">
                <div className="issue-title-wrapper">
                  <p className="issue-title">1.概況と課題</p>
                </div>
                <Markdown className="overview-text" plugins={[gfm]}>
                  {fixCJKBold(acmIssueDetail?.issue)}
                </Markdown>
              </div>
              <div className="issue-factor-wrapper">
                <div className="issue-title-wrapper">
                  <p className="issue-title">2.課題の要因</p>
                </div>
                <p className="factor-title">課題の要因を分析しました。</p>
                <div className="factor-text">
                  <Markdown className="factor-description-text" plugins={[gfm]}>
                    {fixCJKBold(acmIssueDetail?.factor)}
                  </Markdown>
                  <img
                    src={AcmFactorBlurText}
                    className="factor-blur-text"
                    alt="factor-blur-text"
                  />
                  <img
                    src={AcmFactorBlurTable}
                    className="factor-blur-text"
                    alt="factor-blur-table"
                  />
                  <Button
                    onClick={() => {
                      setShowPRModal(true);
                    }}
                    className="cta-button cta-button-large"
                    id="acm-issue-detail-button-2-factor"
                  >
                    要因分析の続きをみる
                  </Button>
                </div>
              </div>
              <div className="issue-action-wrapper">
                <div className="issue-title-wrapper">
                  <p className="issue-title">3.アクションプラン</p>
                  <p className="issue-sub-title">
                    選択した​方​針に​沿って​AIが​アクションプランを​提案します。
                  </p>
                </div>
                <ul className="action-list">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <li key={index} className="action-item">
                      <div className="action-content">
                        <img
                          src={AcmActionBlurText}
                          className="action-blur-text"
                          alt="action-blur-text"
                        />
                      </div>
                    </li>
                  ))}
                  <Button
                    onClick={() => {
                      setShowPRModal(true);
                    }}
                    className="cta-button cta-button-large"
                    id="acm-issue-detail-button-3-action"
                  >
                    アクションプランの続きをみる
                  </Button>
                </ul>
              </div>
              <div className="acm-issue-close-button">
                <Button
                  onClick={() => {
                    window.close();
                  }}
                >
                  閉じる
                </Button>
              </div>
              <Footer />
            </div>
          </div>
          <AcmPRModal
            show={showPRModal}
            onHide={() => {
              setShowPRModal(false);
            }}
          />
        </>
      )}
    </>
  );
}
export default AcmIssueContainer;
